// =============================================================================
// FireISP 5.0 — App Router
// =============================================================================
// Implements role-based UI routing:
//   • Public routes: /login, /forgot-password, /reset-password, /verify-email
//   • Protected routes (any authenticated user): /, /clients, /contracts, etc.
//   • Admin-only routes: /users, /settings
//   • Billing+ routes: /reports
// =============================================================================

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/AuthContext';
import { PrivateRoute } from '@/auth/PrivateRoute';
import { Layout } from '@/components/Layout';
import { Login } from '@/pages/Login';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { VerifyEmail } from '@/pages/VerifyEmail';
import { PortalAuthProvider } from '@/auth/PortalAuthContext';
import { PortalRoute } from '@/auth/PortalRoute';
import { PortalLayout } from '@/components/PortalLayout';
import { PortalLogin } from '@/pages/portal/PortalLogin';
import { PortalForgotPassword } from '@/pages/portal/PortalForgotPassword';
import { PortalResetPassword } from '@/pages/portal/PortalResetPassword';
import { PortalDashboard } from '@/pages/portal/PortalDashboard';
import { PortalInvoices } from '@/pages/portal/PortalInvoices';
import { PortalInvoiceDetail } from '@/pages/portal/PortalInvoiceDetail';
import { PortalTickets } from '@/pages/portal/PortalTickets';
import { PortalTicketDetail } from '@/pages/portal/PortalTicketDetail';
import { PortalAccount } from '@/pages/portal/PortalAccount';
import { PortalKb } from '@/pages/portal/PortalKb';
import { PortalSpeedTest } from '@/pages/portal/PortalSpeedTest';
import { PortalChat } from '@/pages/portal/PortalChat';
import { PortalPrivacy } from '@/pages/portal/PortalPrivacy';
import { Dashboard } from '@/pages/Dashboard';
import { ClientList } from '@/pages/ClientList';
import { ClientDetail } from '@/pages/ClientDetail';
import { ClientGroupList } from '@/pages/ClientGroupList';
import { LeadList } from '@/pages/LeadList';
import { ServiceOrderList } from '@/pages/ServiceOrderList';
import { WinbackCampaignList } from '@/pages/WinbackCampaignList';
import { CommunicationCampaignList } from '@/pages/CommunicationCampaignList';
import { ChurnAnalytics } from '@/pages/ChurnAnalytics';
import { ContractList } from '@/pages/ContractList';
import { ContractDetail } from '@/pages/ContractDetail';
import { InvoiceList } from '@/pages/InvoiceList';
import { InvoiceDetail } from '@/pages/InvoiceDetail';
import { PaymentList } from '@/pages/PaymentList';
import { PaymentDetail } from '@/pages/PaymentDetail';
import { TicketList } from '@/pages/TicketList';
import { TicketDetail } from '@/pages/TicketDetail';
import { FollowUpReminderList } from '@/pages/FollowUpReminderList';
import { SatisfactionSurveyList } from '@/pages/SatisfactionSurveyList';
import { EscalationList } from '@/pages/EscalationList';
import { DeviceMap } from '@/pages/DeviceMap';
import { UserList } from '@/pages/UserList';
import { CfdiList } from '@/pages/CfdiList';
import { PlanList } from '@/pages/PlanList';
import { QuoteList } from '@/pages/QuoteList';
import { QuoteDetail } from '@/pages/QuoteDetail';
import { CreditNoteList } from '@/pages/CreditNoteList';
import { ExpenseList } from '@/pages/ExpenseList';
import { InventoryList } from '@/pages/InventoryList';
import { WarehouseList } from '@/pages/WarehouseList';
import { VendorList } from '@/pages/VendorList';
import { PurchaseOrderList } from '@/pages/PurchaseOrderList';
import { PurchaseOrderDetail } from '@/pages/PurchaseOrderDetail';
import { CoverageZoneMap } from '@/pages/CoverageZoneMap';
import { RadiusSessions } from '@/pages/RadiusSessions';
import { SessionAccounting } from '@/pages/SessionAccounting';
import { SnmpMetrics } from '@/pages/SnmpMetrics';
import { SnmpTraps } from '@/pages/SnmpTraps';
import { SiteList } from '@/pages/SiteList';
import { SiteDetail } from '@/pages/SiteDetail';
import { NasList } from '@/pages/NasList';
import { NasDetail } from '@/pages/NasDetail';
import { DeviceDetail } from '@/pages/DeviceDetail';
import { MacMoveEvents } from '@/pages/MacMoveEvents';
import { PppoeServiceProfileList } from '@/pages/PppoeServiceProfileList';
import { PppoeDiagnostics } from '@/pages/PppoeDiagnostics';
import { DhcpServerList } from '@/pages/DhcpServerList';
import { NatManagementList } from '@/pages/NatManagementList';
import { PtrRecordList } from '@/pages/PtrRecordList';
import { Ipv6ManagementPage } from '@/pages/Ipv6ManagementPage';
import { TransitionMechanismsPage } from '@/pages/TransitionMechanismsPage';
import { DeviceGroupList } from '@/pages/DeviceGroupList';
import { DiscoveryScanList } from '@/pages/DiscoveryScanList';
import { DeviceImport } from '@/pages/DeviceImport';
import { DataImport } from '@/pages/DataImport';
import { TrapForwardingRuleList } from '@/pages/TrapForwardingRuleList';
import { PollerNodeList } from '@/pages/PollerNodeList';
import { DevicePollingConfigList } from '@/pages/DevicePollingConfigList';
import { PollerPerformanceDashboard } from '@/pages/PollerPerformanceDashboard';
import { AlertEscalationChainList } from '@/pages/AlertEscalationChainList';
import { MaintenanceWindowList } from '@/pages/MaintenanceWindowList';
import { AlertChannelList } from '@/pages/AlertChannelList';
import { AlertSuppressionList } from '@/pages/AlertSuppressionList';
import { IpPoolList } from '@/pages/IpPoolList';
import { IpAssignmentList } from '@/pages/IpAssignmentList';
import { VlanList } from '@/pages/VlanList';
import { ServiceAreaList } from '@/pages/ServiceAreaList';
import { OutageList } from '@/pages/OutageList';
import { SpeedTestList } from '@/pages/SpeedTestList';
import { ConnectionLogList } from '@/pages/ConnectionLogList';
import { NetworkHealthList } from '@/pages/NetworkHealthList';
import { SnmpProfileList } from '@/pages/SnmpProfileList';
import { DeviceConfigBackupList } from '@/pages/DeviceConfigBackupList';
import { ConfigTemplateList } from '@/pages/ConfigTemplateList';
import { ConfigBackupScheduleList } from '@/pages/ConfigBackupScheduleList';
import { ConfigComplianceRuleList } from '@/pages/ConfigComplianceRuleList';
import { SuspensionRuleList } from '@/pages/SuspensionRuleList';
import { SuspensionConsole } from '@/pages/SuspensionConsole';
import { TechnicianMap } from '@/pages/TechnicianMap';
import { Reports } from '@/pages/Reports';
import { AnalyticsDashboard } from '@/pages/AnalyticsDashboard';
import { TaxReports } from '@/pages/TaxReports';
import { InvoiceSettings } from '@/pages/InvoiceSettings';
import { LateFeeRuleList } from '@/pages/LateFeeRuleList';
import { PaymentReminderSettings } from '@/pages/PaymentReminderSettings';
import { Settings } from '@/pages/Settings';
import { ProfecoComplaints } from '@/pages/ProfecoComplaints';
import RegulatoryCompliancePage from '@/pages/RegulatoryCompliancePage';
import { SniiInfrastructureReportingPage } from '@/pages/SniiInfrastructureReportingPage';
import { SlaDefinitionList } from '@/pages/SlaDefinitionList';
import { RoleList } from '@/pages/RoleList';
import { ApiTokenList } from '@/pages/ApiTokenList';
import { WebhookList } from '@/pages/WebhookList';
import { AuditLogList } from '@/pages/AuditLogList';
import { ScheduledTaskList } from '@/pages/ScheduledTaskList';
import { OrganizationList } from '@/pages/OrganizationList';
import { OrganizationDetail } from '@/pages/OrganizationDetail';
import { DsarTool } from '@/pages/DsarTool';
import { DrDrillStatus } from '@/pages/DrDrillStatus';
import { BackupSettings } from '@/pages/BackupSettings';
import { QueueStats } from '@/pages/QueueStats';
import { CsdCertificateList } from '@/pages/CsdCertificateList';
import { SubscriberCertificateList } from '@/pages/SubscriberCertificateList';
import { PacProviderList } from '@/pages/PacProviderList';
import { SatCatalogList } from '@/pages/SatCatalogList';
import { RegulatoryFilingList } from '@/pages/RegulatoryFilingList';
import { ConcessionTitleList } from '@/pages/ConcessionTitleList';
import { IftStatisticalReportList } from '@/pages/IftStatisticalReportList';
import { FacturaPublicaList } from '@/pages/FacturaPublicaList';
import { MessageTemplateList } from '@/pages/MessageTemplateList';
import { DocumentTemplates } from '@/pages/DocumentTemplates';
import { PromotionList } from '@/pages/PromotionList';
import { TaxRuleList } from '@/pages/TaxRuleList';
import { TaxRateList } from '@/pages/TaxRateList';
import { PaymentGatewayList } from '@/pages/PaymentGatewayList';
import { PaymentTransactionList } from '@/pages/PaymentTransactionList';
import { RecurringPaymentProfileList } from '@/pages/RecurringPaymentProfileList';
import { PaymentPlanList } from '@/pages/PaymentPlanList';
import { CashReconciliationList } from '@/pages/CashReconciliationList';
import { RefundRequestList } from '@/pages/RefundRequestList';
import { BillingDisputeList } from '@/pages/BillingDisputeList';
import { ChargebackList } from '@/pages/ChargebackList';
import { BillingAdjustmentList } from '@/pages/BillingAdjustmentList';
import { AIAssistantSettings } from '@/pages/AIAssistantSettings';
import { OltManagementPage } from '@/pages/OltManagementPage';
import { OnuManagementPage } from '@/pages/OnuManagementPage';
import { PonPortManagementPage } from '@/pages/PonPortManagementPage';
import { FiberPlantManagementPage } from '@/pages/FiberPlantManagementPage';
import { CpeManagementPage } from '@/pages/CpeManagementPage';
import { CpeProfilesPage } from '@/pages/CpeProfilesPage';
import { CpeDiagnosticsPage } from '@/pages/CpeDiagnosticsPage';
import { CpeInventoryPage } from '@/pages/CpeInventoryPage';
import { WirelessManagementPage } from '@/pages/WirelessManagementPage';
import { WirelessMetricsPage } from '@/pages/WirelessMetricsPage';
import { QosBandwidthPage } from '@/pages/QosBandwidthPage';
import { NocDashboard } from '@/pages/NocDashboard';
import { WorkOrders } from '@/pages/WorkOrders';
import { TopologyMapPage } from '@/pages/TopologyMapPage';
import { InventoryManagement } from '@/pages/InventoryManagement';
import { HubPage } from '@/pages/HubPage';
import { NotFound } from '@/pages/NotFound';
import { SecurityAccessControlPage } from '@/pages/SecurityAccessControlPage';
import AutomationPage from '@/pages/AutomationPage';
import ResellerPage from '@/pages/ResellerPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { AiSupportPage } from '@/pages/AiSupportPage';
import { UserWgTunnels } from '@/pages/UserWgTunnels';
import { AdminWgTunnels } from '@/pages/AdminWgTunnels';
import { DarkModeProvider } from '@/auth/DarkModeContext';
import { AccentProvider } from '@/auth/AccentContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export function App() {
  return (
    <DarkModeProvider>
      <AccentProvider>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PortalAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* ---- Client Self-Service Portal ---- */}
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
              <Route path="/portal/reset-password" element={<PortalResetPassword />} />
              <Route element={<PortalRoute />}>
                <Route element={<PortalLayout />}>
                  <Route path="/portal" element={<PortalDashboard />} />
                  <Route path="/portal/invoices" element={<PortalInvoices />} />
                  <Route path="/portal/invoices/:id" element={<PortalInvoiceDetail />} />
                  <Route path="/portal/tickets" element={<PortalTickets />} />
                  <Route path="/portal/tickets/:id" element={<PortalTicketDetail />} />
                  <Route path="/portal/account" element={<PortalAccount />} />
                  <Route path="/portal/kb" element={<PortalKb />} />
                  <Route path="/portal/speed-test" element={<PortalSpeedTest />} />
                  <Route path="/portal/chat" element={<PortalChat />} />
                  <Route path="/portal/privacy" element={<PortalPrivacy />} />
                </Route>
              </Route>

            {/* Protected — any authenticated user */}
            <Route element={<PrivateRoute />}>
              <Route element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="clients" element={<ClientList />} />
                <Route path="clients/:id" element={<ClientDetail />} />
                <Route path="client-groups" element={<ClientGroupList />} />
                <Route path="leads" element={<LeadList />} />
                <Route path="service-orders" element={<ServiceOrderList />} />
                <Route path="contracts" element={<ContractList />} />
                <Route path="contracts/:id" element={<ContractDetail />} />
                <Route path="invoices" element={<InvoiceList />} />
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="payments" element={<PaymentList />} />
                <Route path="payments/:id" element={<PaymentDetail />} />
                <Route path="tickets" element={<TicketList />} />
                <Route path="tickets/:id" element={<TicketDetail />} />
                <Route path="communication-campaigns" element={<CommunicationCampaignList />} />
                <Route path="follow-up-reminders" element={<FollowUpReminderList />} />
                <Route path="satisfaction-surveys" element={<SatisfactionSurveyList />} />
                <Route path="escalations" element={<EscalationList />} />
                <Route path="devices" element={<DeviceMap />} />
                <Route path="devices/:id" element={<DeviceDetail />} />
                <Route path="wg-tunnels" element={<UserWgTunnels />} />
                {/* any-auth so support can reach them (backend gates via
                    outages.view / network_health.view — support has both,
                    migration 377) */}
                <Route path="outages" element={<OutageList />} />
                <Route path="network-health" element={<NetworkHealthList />} />
                {/* Backend pppoe.diagnostics / MAC-move permissions determine
                    which datasets each authenticated role may read. */}
                <Route path="pppoe-diagnostics" element={<PppoeDiagnostics />} />
                {/* Subscriber accounting is available to every authenticated
                    persona whose resolved backend permissions allow it. */}
                <Route path="connection-logs" element={<ConnectionLogList />} />
                {/* Tab visibility and every API operation are permission-gated;
                    this route also supports custom legal-response principals. */}
                <Route path="regulatory-compliance" element={<RegulatoryCompliancePage />} />
                {/* Exact infrastructure geolocation and every action are
                    permission-gated inside the MX-only SNII workflow. */}
                <Route path="snii-infrastructure" element={<SniiInfrastructureReportingPage />} />
              </Route>
            </Route>

            {/* Technician+ — technician, billing, or admin */}
            <Route element={<PrivateRoute requiredRole="technician" />}>
              <Route element={<Layout />}>
                <Route path="network" element={<HubPage section="network" />} />
                <Route path="inventory" element={<InventoryList />} />
                <Route path="warehouses" element={<WarehouseList />} />
                <Route path="vendors" element={<VendorList />} />
                <Route path="purchase-orders" element={<PurchaseOrderList />} />
                <Route path="purchase-orders/:id" element={<PurchaseOrderDetail />} />
                <Route path="radius-sessions" element={<RadiusSessions />} />
                <Route path="subscriber-certificates" element={<SubscriberCertificateList />} />
                <Route path="session-accounting" element={<SessionAccounting />} />
                <Route path="snmp-metrics" element={<SnmpMetrics />} />
                <Route path="snmp-traps" element={<SnmpTraps />} />
                <Route path="coverage-zones" element={<CoverageZoneMap />} />
                <Route path="sites" element={<SiteList />} />
                <Route path="sites/:id" element={<SiteDetail />} />
                <Route path="nas" element={<NasList />} />
                <Route path="nas/:id" element={<NasDetail />} />
                <Route path="mac-move-events" element={<MacMoveEvents />} />
                <Route path="pppoe-service-profiles" element={<PppoeServiceProfileList />} />
                <Route path="ip-pools" element={<IpPoolList />} />
                <Route path="ip-assignments" element={<IpAssignmentList />} />
                <Route path="vlans" element={<VlanList />} />
                <Route path="service-areas" element={<ServiceAreaList />} />
                <Route path="speed-tests" element={<SpeedTestList />} />
                <Route path="snmp-profiles" element={<SnmpProfileList />} />
                <Route path="device-config-backups" element={<DeviceConfigBackupList />} />
                <Route path="config-templates" element={<ConfigTemplateList />} />
                <Route path="config-backup-schedules" element={<ConfigBackupScheduleList />} />
                <Route path="config-compliance-rules" element={<ConfigComplianceRuleList />} />
                <Route path="suspension-rules" element={<SuspensionRuleList />} />
                <Route path="suspension-console" element={<SuspensionConsole />} />
                <Route path="technician-map" element={<TechnicianMap />} />
                <Route path="dhcp-servers" element={<DhcpServerList />} />
                <Route path="nat-management" element={<NatManagementList />} />
                <Route path="ptr-records" element={<PtrRecordList />} />
                <Route path="ipv6-management" element={<Ipv6ManagementPage />} />
                <Route path="transition-mechanisms" element={<TransitionMechanismsPage />} />
                <Route path="device-groups" element={<DeviceGroupList />} />
                <Route path="discovery-scans" element={<DiscoveryScanList />} />
                <Route path="device-import" element={<DeviceImport />} />
                <Route path="trap-forwarding-rules" element={<TrapForwardingRuleList />} />
                <Route path="poller-nodes" element={<PollerNodeList />} />
                <Route path="device-polling-configs" element={<DevicePollingConfigList />} />
                <Route path="poller-performance" element={<PollerPerformanceDashboard />} />
                <Route path="alert-escalation-chains" element={<AlertEscalationChainList />} />
                <Route path="maintenance-windows" element={<MaintenanceWindowList />} />
                <Route path="alert-channels" element={<AlertChannelList />} />
                <Route path="alert-suppression-rules" element={<AlertSuppressionList />} />
                <Route path="olt-management" element={<OltManagementPage />} />
                <Route path="onu-management" element={<OnuManagementPage />} />
                <Route path="pon-port-management" element={<PonPortManagementPage />} />
                <Route path="fiber-plant-management" element={<FiberPlantManagementPage />} />
                <Route path="cpe-management" element={<CpeManagementPage />} />
                <Route path="cpe-profiles" element={<CpeProfilesPage />} />
                <Route path="cpe-diagnostics" element={<CpeDiagnosticsPage />} />
                <Route path="cpe-inventory" element={<CpeInventoryPage />} />
                <Route path="wireless" element={<WirelessManagementPage />} />
                <Route path="wireless-metrics" element={<WirelessMetricsPage />} />
                <Route path="qos-bandwidth" element={<QosBandwidthPage />} />
                <Route path="noc-dashboard" element={<NocDashboard />} />
                <Route path="work-orders" element={<WorkOrders />} />
                <Route path="topology-map" element={<TopologyMapPage />} />
                <Route path="inventory-management" element={<InventoryManagement />} />
              </Route>
            </Route>

            {/* Billing+ — billing or admin */}
            <Route element={<PrivateRoute requiredRole="billing" />}>
              <Route element={<Layout />}>
                <Route path="billing" element={<HubPage section="billing" />} />
                <Route path="cfdi" element={<CfdiList />} />
                <Route path="plans" element={<PlanList />} />
                <Route path="quotes" element={<QuoteList />} />
                <Route path="quotes/:id" element={<QuoteDetail />} />
                <Route path="credit-notes" element={<CreditNoteList />} />
                <Route path="expenses" element={<ExpenseList />} />
                <Route path="promotions" element={<PromotionList />} />
                <Route path="tax-rules" element={<TaxRuleList />} />
                <Route path="tax-rates" element={<TaxRateList />} />
                <Route path="payment-gateways" element={<PaymentGatewayList />} />
                <Route path="payment-transactions" element={<PaymentTransactionList />} />
                <Route path="recurring-payment-profiles" element={<RecurringPaymentProfileList />} />
                <Route path="payment-plans" element={<PaymentPlanList />} />
                <Route path="cash-reconciliation" element={<CashReconciliationList />} />
                <Route path="refund-requests" element={<RefundRequestList />} />
                <Route path="billing-disputes" element={<BillingDisputeList />} />
                <Route path="chargebacks" element={<ChargebackList />} />
                <Route path="billing-adjustments" element={<BillingAdjustmentList />} />
                <Route path="winback-campaigns" element={<WinbackCampaignList />} />
                <Route path="churn-analytics" element={<ChurnAnalytics />} />
                <Route path="csd-certificates" element={<CsdCertificateList />} />
                <Route path="pac-providers" element={<PacProviderList />} />
                <Route path="sat-catalogs" element={<SatCatalogList />} />
                <Route path="regulatory-filings" element={<RegulatoryFilingList />} />
                <Route path="concession-titles" element={<ConcessionTitleList />} />
                <Route path="ift-statistical-reports" element={<IftStatisticalReportList />} />
                <Route path="facturas-publicas" element={<FacturaPublicaList />} />
                <Route path="reports" element={<Reports />} />
                <Route path="analytics-dashboard" element={<AnalyticsDashboard />} />
                <Route path="tax-reports" element={<TaxReports />} />
                <Route path="invoice-settings" element={<InvoiceSettings />} />
                <Route path="data-import" element={<DataImport />} />
                <Route path="late-fee-rules" element={<LateFeeRuleList />} />
                <Route path="payment-reminder-settings" element={<PaymentReminderSettings />} />
                <Route path="profeco-complaints" element={<ProfecoComplaints />} />
              </Route>
            </Route>

            {/* Admin-only */}
            <Route element={<PrivateRoute requiredRole="admin" />}>
              <Route element={<Layout />}>
                <Route path="admin" element={<HubPage section="admin" />} />
                <Route path="users" element={<UserList />} />
                <Route path="organizations" element={<OrganizationList />} />
                <Route path="organizations/:id" element={<OrganizationDetail />} />
                <Route path="dsar" element={<DsarTool />} />
                <Route path="dr-drill" element={<DrDrillStatus />} />
                <Route path="backups" element={<BackupSettings />} />
                <Route path="sla-definitions" element={<SlaDefinitionList />} />
                <Route path="roles" element={<RoleList />} />
                <Route path="api-tokens" element={<ApiTokenList />} />
                <Route path="webhooks" element={<WebhookList />} />
                <Route path="audit-logs" element={<AuditLogList />} />
                <Route path="scheduled-tasks" element={<ScheduledTaskList />} />
                <Route path="queue-stats" element={<QueueStats />} />
                <Route path="settings" element={<Settings />} />
                <Route path="message-templates" element={<MessageTemplateList />} />
                <Route path="document-templates" element={<DocumentTemplates />} />
                <Route path="ai-assistant" element={<AIAssistantSettings />} />
                <Route path="security-access-control" element={<SecurityAccessControlPage />} />
                <Route path="automation" element={<AutomationPage />} />
                <Route path="resellers" element={<ResellerPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="ai-support" element={<AiSupportPage />} />
                <Route path="admin/user-tunnels" element={<AdminWgTunnels />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </BrowserRouter>
        </PortalAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
      </AccentProvider>
    </DarkModeProvider>
  );
}
